import { type SessionRefusalCode, sessionErrorSchema } from '@matter_extractor/shared';
import type { SessionRow } from '@matter_extractor/shared';
import { ApiError } from './api';
import { type ReviewEntrySession, reviewAuthorName, reviewEntryState } from './review-entry';
import { isProdOrigin } from './session-origin';

/**
 * What the screens say of the INTERRUPTION of a session (issue #199,
 * interruption.md) - pure, in the voice of the maquette `interruption.html`:
 * the link in the sentence under « Relecture », the box and its refusals, the
 * last activity it reads out, the name of whoever interrupted.
 *
 * Nothing here draws or fetches. The dates are written by hand, in the régime
 * of `session-labels.ts`: `Intl` answers according to the machine's locale, and
 * a test and a capture would not say the same thing on two runners.
 */

/* ------------------------------------------------------------------ the entry */

/** The words of the link, the one gesture of the sentence (D8). */
export const INTERRUPT_LINK_LABEL = 'Interrompre la session';

/** The sentence a session of the prod gets on the atelier, in place of the link (§5). */
export const PROD_INTERRUPT_LEAD = 'Une session de la prod s’interrompt sur la prod';

/** The words of the link that leads there. */
export const PROD_INTERRUPT_LINK_LABEL = 'l’ouvrir sur la prod ↗';

/** The three shapes the gesture takes on a row. */
export type InterruptEntry =
  /** No gesture: the session is over, in the trash, or has no protocol page. */
  | { readonly kind: 'none' }
  /** The link that opens the box. */
  | { readonly kind: 'link' }
  /** The atelier, on a session of the prod: the sentence, and the link when the prod is known. */
  | { readonly kind: 'prod'; readonly href: string | null };

/** What the row says of a session, for the gesture: the review's entry, the trash, the origin. */
export type InterruptEntrySession = ReviewEntrySession &
  Pick<SessionRow, 'id' | 'deletedAt'> & { readonly origin?: SessionRow['origin'] };

/**
 * Where the gesture stands on a row (§4.1, §5): only where the sentence under
 * the disabled « Relecture » exists - a session IN PROGRESS whose frozen
 * experience has a protocol page - and never in the trash, where one restores
 * first. A session of the prod is interrupted on the prod: the atelier offers
 * the way there instead.
 */
export function interruptEntry(
  session: InterruptEntrySession,
  prodOrigin: string | null,
): InterruptEntry {
  if (reviewEntryState(session) !== 'disabled') return { kind: 'none' };
  if (session.deletedAt !== null) return { kind: 'none' };
  if (isProdOrigin(session)) {
    return {
      kind: 'prod',
      href: prodOrigin === null ? null : prodSessionUrl(prodOrigin, session.id),
    };
  }
  return { kind: 'link' };
}

/** The same session on the prod, in the address bar of the operator's other tab. */
export function prodSessionUrl(prodOrigin: string, sessionId: string): string {
  return `${prodOrigin.replace(/\/+$/, '')}/admin/sessions/${encodeURIComponent(sessionId)}`;
}

/* ----------------------------------------------------------------- the author */

/**
 * « alice » out of `alice@patrickmathieu.net`; « jeton partagé » as it is
 * (§4.4, H7) - the régime of the review's marks. Null when nobody signed the
 * session: an older answer, or a status that is not `interrupted`.
 */
export function interruptionAuthor(endedBy: string | null | undefined): string | null {
  if (endedBy === null || endedBy === undefined || endedBy === '') return null;
  return reviewAuthorName(endedBy);
}

/* ------------------------------------------------------------ last activity */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * « il y a 2 h 14 », or « à l'instant » under a minute (§4.2, H9).
 *
 * Minutes alone under the hour (« il y a 12 min »), hours and minutes past it
 * with the minutes dropped when they are nothing (« il y a 3 h »), and days
 * from the twenty-fourth hour - a session in progress is never that old, but
 * a row read from a clock that drifted must not print a nonsense.
 */
export function relativeAgo(then: Date, now: Date): string {
  const elapsed = now.getTime() - then.getTime();
  if (Number.isNaN(elapsed) || elapsed < MINUTE_MS) return 'à l’instant';
  if (elapsed < HOUR_MS) return `il y a ${Math.floor(elapsed / MINUTE_MS)} min`;
  if (elapsed < DAY_MS) {
    const hours = Math.floor(elapsed / HOUR_MS);
    const minutes = Math.floor((elapsed % HOUR_MS) / MINUTE_MS);
    return minutes === 0
      ? `il y a ${hours} h`
      : `il y a ${hours} h ${String(minutes).padStart(2, '0')}`;
  }
  const days = Math.floor(elapsed / DAY_MS);
  const hours = Math.floor((elapsed % DAY_MS) / HOUR_MS);
  return hours === 0 ? `il y a ${days} j` : `il y a ${days} j ${hours} h`;
}

/**
 * The moment of the last trace the session recorded: its start plus the
 * duration the list reads (`durationMs` runs from the start to the last turn,
 * the last page or the end, and never to `now`).
 */
export function lastActivityAt(row: Pick<SessionRow, 'startedAt' | 'durationMs'>): Date {
  return new Date(Date.parse(row.startedAt) + row.durationMs);
}

/* ------------------------------------------------------------- the refusals */

/** One refusal of the box: its sentence, and whether pressing the button again could change it. */
export interface InterruptionRefusal {
  readonly code: SessionRefusalCode | null;
  readonly sentence: string;
  /** True when the refusal is a state of the session, not a moment: only « Fermer » is left. */
  readonly final: boolean;
}

const NOT_INTERRUPTED = 'La session n’a pas été interrompue.';

/**
 * Each refusal of `POST /api/sessions/{id}/interruption`, worded from its
 * `code` and never from the server's sentence (§4.3). A failure that is not a
 * business refusal - the network, a 5xx - says it was not interrupted and
 * invites a second try.
 */
export function interruptionRefusalSentence(code: SessionRefusalCode | null): {
  readonly sentence: string;
  readonly final: boolean;
} {
  switch (code) {
    case 'turn-in-flight':
      return {
        sentence: `${NOT_INTERRUPTED} Un tour est en train de se jouer : la personne vient de répondre. Réessayez dans quelques secondes, ou laissez-la continuer.`,
        final: false,
      };
    case 'session-over':
      return {
        sentence: `${NOT_INTERRUPTED} Elle est déjà finie : l’écran se relit et montre son statut, la relecture est ouverte.`,
        final: true,
      };
    case 'session-in-trash':
      return {
        sentence: `${NOT_INTERRUPTED} Elle est à la corbeille : restaurez-la d’abord.`,
        final: true,
      };
    case 'no-protocol-page':
      return {
        sentence: `${NOT_INTERRUPTED} L’expérience jouée n’a pas de page protocole : aucune relecture n’est possible pour cette session.`,
        final: true,
      };
    case 'session-from-prod':
      return {
        sentence: `${NOT_INTERRUPTED} Elle vient de la prod : elle s’interrompt sur la prod.`,
        final: true,
      };
    default:
      return { sentence: `${NOT_INTERRUPTED} Réessayez dans un instant.`, final: false };
  }
}

/** Reads a failed call as the refusal it is, or as a failure with no code. */
export function interruptionRefusal(error: unknown): InterruptionRefusal {
  if (error instanceof ApiError && error.status === 404) {
    return {
      code: null,
      sentence: `${NOT_INTERRUPTED} Cette session n’existe plus.`,
      final: true,
    };
  }
  const refusal =
    error instanceof ApiError && error.status === 409
      ? sessionErrorSchema.safeParse(error.details)
      : null;
  const code = refusal?.success ? refusal.data.code : null;
  return { code, ...interruptionRefusalSentence(code) };
}
